import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { makeApiCall } from "src/api/apiRequests";
import moment from "moment";

import StoreDiscount from "./StoreDiscount";
import ShowDiscount from "./ShowDiscount";
import UpdateDiscount from "./UpdateDiscount";

import API_PATHS from "src/api/apiPaths";
import API_HEADERS from "src/api/apiConfig";
import ListResponse from "src/helpers/ListResponse";
import FLASH_MESSAGES from "src/helpers/FlashMessages";
import Loading from "src/helpers/Loading";
import CustomPagination from "src/helpers/CustomPaginate";
import SearchFilter from "src/helpers/SearchFilter";
import * as ConfirmPopup from "../../../../helpers/ConfirmPopup";
import { PermissionCheck } from "src/helpers/PermissionCheck";
import { LanguageDate } from "src/helpers/CheckLanguageDate";

const CategoriesDiscountList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [discount, setDiscount] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activePage, setActivePage] = useState<number>(1);

  const [storeDiscountModal, setStoreDiscountModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalId, setModalId] = useState<number | null>(null);

  const getDiscounts = async (): Promise<void> => {
    let request: any = API_PATHS.discountList;

    var searchParams = {};

    if (activePage > 1) {
      searchParams = {
        page: activePage,
      };
    }

    request = SearchFilter(searchParams, request);

    try {
      const response: any = await makeApiCall<ResponseType>(
        request,
        "GET",
        API_HEADERS.authenticated
      );
      setDiscount(response.discounts);
      setLoading(false);
    } catch (error: any) {
      FLASH_MESSAGES.errorMsg(error.response.data.error);
      navigate(-1);
    }
  };

  useEffect(() => {
    getDiscounts();
  }, [activePage]);

  const deleteDiscount = (discountId: number, discountTitle: string) => {
    ConfirmPopup.deletePopup(
      t("delete_discount_title"),
      t("delete_discount_type"),
      discountTitle
    ).then(async (response) => {
      if (response.isConfirmed) {
        try {
          const response: any = await makeApiCall<ResponseType>(
            API_PATHS.discountDelete(discountId),
            "DELETE",
            API_HEADERS.authenticated
          );

          getDiscounts();
          FLASH_MESSAGES.successMsg(response.success);
        } catch (error: any) {
          if (error.response.data.error) {
            FLASH_MESSAGES.errorMsg(error.response.data.error);
          }
        }
      }
    });
  };

  return (
    <>
      {PermissionCheck("discount-store") ? (
        <div className="w-100 mb-5 d-flex justify-content-end">
          <button
            className="button-blue has-icon"
            onClick={() => setStoreDiscountModal(true)}
          >
            <i className="icon add-icon" />
            {t("discount_store_new")}
          </button>
          {storeDiscountModal && (
            <StoreDiscount
              onShowModal={storeDiscountModal}
              setShowModal={(e) => setStoreDiscountModal(e)}
              setModified={() => getDiscounts()}
            />
          )}
        </div>
      ) : (
        ""
      )}
      <div className="table-list mb-5">
        <table role="table" className="w-100">
          <thead>
            <tr role="row">
              <th
                style={{ width: "25%" }}
                role="columnheader"
                className="text-left"
              >
                <i className="icon category-icon" />
                {t("discount_percentge")}
              </th>
              <th
                style={{ width: "25%" }}
                role="columnheader"
                className="text-center"
              >
                <i className="icon calender-icon" />
                {t("discount_start_date")}
              </th>
              <th
                style={{ width: "25%" }}
                role="columnheader"
                className="text-center"
              >
                <i className="icon calender-icon" />
                {t("discount_end_date")}
              </th>
              <th
                style={{ width: "25%" }}
                role="columnheader"
                className="text-center"
              >
                <i className="icon actions-icon" />
                {t("discount_actions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="text-center" colSpan={4}>
                  <Loading />
                </td>
              </tr>
            ) : (
              discount?.data?.map((dis: any) => (
                <tr key={dis.id}>
                  <td
                    role="cell"
                    className="text-left bold-text"
                    data-label={t("discount_percentge")}
                  >
                    {dis.percentage}%
                  </td>
                  <td
                    role="cell"
                    className="text-center bold-text"
                    data-label={t("discount_start_date")}
                  >
                    {dis.start_date
                      ? moment(dis.start_date)
                          .locale(LanguageDate.moment)
                          .format("DD MMMM, yyyy")
                      : "-"}
                  </td>
                  <td
                    role="cell"
                    className="text-center bold-text"
                    data-label={t("discount_end_date")}
                  >
                    {dis.end_date
                      ? moment(dis.end_date)
                          .locale(LanguageDate.moment)
                          .format("DD MMMM, yyyy")
                      : "-"}
                  </td>
                  <td
                    role="cell"
                    className="text-center"
                    data-label={t("discount_actions")}
                  >
                    <div className="actions">
                      {PermissionCheck("discount-update") &&
                      PermissionCheck("discount-edit") ? (
                        <button
                          className="button-unstyled-icon"
                          onClick={() => {
                            setShowEditModal(true);
                            setModalId(dis.id);
                          }}
                        >
                          <i className="icon edit-icon" />
                        </button>
                      ) : (
                        ""
                      )}

                      {showEditModal && modalId === dis.id && (
                        <UpdateDiscount
                          onShowModal={showEditModal}
                          setShowModal={(e) => {
                            setShowEditModal(e);
                            setModalId(null);
                          }}
                          discountId={dis.id}
                          setModified={() => getDiscounts()}
                        />
                      )}
                      {PermissionCheck("discount-edit") ? (
                        <button
                          className="button-unstyled-icon"
                          onClick={() => {
                            setShowModal(true);
                            setModalId(dis.id);
                          }}
                        >
                          <i className="icon show-icon" />
                        </button>
                      ) : (
                        ""
                      )}
                      {showModal && modalId === dis.id && (
                        <ShowDiscount
                          onShowModal={showModal}
                          setModalVisible={(e) => {
                            setShowModal(e);
                            setModalId(null);
                          }}
                          discountId={dis.id}
                          setShowModal={setShowEditModal}
                        />
                      )}

                      {PermissionCheck("discount-delete") ? (
                        <button
                          className="button-unstyled-icon"
                          onClick={() =>
                            deleteDiscount(discount.id, discount.title)
                          }
                        >
                          <i className="icon delete-icon" />
                        </button>
                      ) : (
                        ""
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <ListResponse totalItems={discount?.data?.length} loading={loading} />
      </div>
      <CustomPagination
        data={discount}
        setActivePage={(e) => setActivePage(e)}
      />
    </>
  );
};

export default CategoriesDiscountList;
